import React, { useState, useEffect } from 'react';
import {
  Trash2, Edit, Plus, X, Phone, Mail, Users,
  AlertCircle, Eye, Shield, CheckCircle, XCircle,
  Search, Filter, Download, RefreshCw, Calendar
} from 'lucide-react';
import Sidebar from '../components/Sidebar';

const handleLogout = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem('token');
  }
  window.location.href = '/';
};

const ObservateurPage = () => {
  const [observateurs, setObservateurs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [observateurToDelete, setObservateurToDelete] = useState(null);
  const [editingObservateur, setEditingObservateur] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [newObservateur, setNewObservateur] = useState({
    nom: '',
    telephone: '',
    email: '',
    motDePasse: '',
    confirmPassword: '',
    actif: true
  });

  // Get token from localStorage
  const token = typeof window !== 'undefined' ? window.localStorage?.getItem('token') : null;
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchObservateurs = async () => {
    try {
      setLoading(true);
      const res = await fetch('    https://vmi1977988.contaboserver.net/api2/observateurs', { headers });
      if (!res.ok) throw new Error('Erreur lors du chargement des observateurs');
      const data = await res.json();
      setObservateurs(data);
      setError('');
    } catch (error) {
      console.error('Erreur fetchObservateurs:', error);
      setError('Impossible de charger les observateurs');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!newObservateur.nom.trim()) {
      setError('Le nom est requis');
      return false;
    }
    if (!newObservateur.email.trim()) {
      setError("L'email est requis");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newObservateur.email)) {
      setError("Format d'email invalide");
      return false;
    }

    if (!newObservateur.telephone.trim()) {
      setError('Le téléphone est requis');
      return false;
    }

    if (!editingObservateur && !newObservateur.motDePasse) {
      setError('Le mot de passe est requis');
      return false;
    }

    if (newObservateur.motDePasse && newObservateur.motDePasse !== newObservateur.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return false;
    }

    if (newObservateur.motDePasse && newObservateur.motDePasse.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }

    return true;
  };

  const handleCreateOrUpdateObservateur = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const url = editingObservateur
        ? `     https://vmi1977988.contaboserver.net/api2/observateurs/${editingObservateur._id}`
        : '    https://vmi1977988.contaboserver.net/api2/observateurs';

      const method = editingObservateur ? 'PUT' : 'POST';

      const dataToSend = {
        nom: newObservateur.nom,
        telephone: newObservateur.telephone,
        email: newObservateur.email,
        actif: newObservateur.actif
      };

      if (newObservateur.motDePasse) {
        dataToSend.motDePasse = newObservateur.motDePasse;
      }

      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(dataToSend)
      });

      if (res.ok) {
        resetForm();
        await fetchObservateurs();
        setShowModal(false);
        setError('');
      } else {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur création/modification observateur:', error);
      setError(error.message || "Impossible de sauvegarder l'observateur");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteObservateur = async () => {
    if (!observateurToDelete) return;

    setLoading(true);
    try {
      const res = await fetch(`     https://vmi1977988.contaboserver.net/api2/observateurs/${observateurToDelete._id}`, {
        method: 'DELETE',
        headers
      });

      if (res.ok) {
        await fetchObservateurs();
        setShowDeleteModal(false);
        setObservateurToDelete(null);
        setError('');
      } else {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression observateur:', error);
      setError("Impossible de supprimer l'observateur");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (observateur) => {
    try {
      setLoading(true);
      const res = await fetch(`     https://vmi1977988.contaboserver.net/api2/observateurs/${observateur._id}/actif`, {
        method: 'PATCH',
        headers
      });

      if (res.ok) {
        await fetchObservateurs();
        setError('');
      } else {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Erreur lors de la modification du statut');
      }
    } catch (error) {
      console.error('Erreur toggle actif:', error);
      setError('Impossible de modifier le statut');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewObservateur({
      nom: '',
      telephone: '',
      email: '',
      motDePasse: '',
      confirmPassword: '',
      actif: true
    });
    setEditingObservateur(null);
    setShowPassword(false);
    setError('');
  };

  const openEditModal = (observateur) => {
    setEditingObservateur(observateur);
    setNewObservateur({
      nom: observateur.nom || '',
      telephone: observateur.telephone || '',
      email: observateur.email || '',
      motDePasse: '',
      confirmPassword: '',
      actif: observateur.actif !== false
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const filteredObservateurs = observateurs.filter(obs => {
    const matchesSearch = obs.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      obs.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      obs.telephone?.includes(searchTerm);

    if (filterStatus === 'active') return matchesSearch && obs.actif;
    if (filterStatus === 'inactive') return matchesSearch && !obs.actif;
    return matchesSearch;
  });

  useEffect(() => {
    fetchObservateurs();
  }, []);

  return (
    <div className="commercial-page">
      <Sidebar onLogout={handleLogout} />

      <div className="container">
        {/* Header */}
        <div className="header-card">
          <div className="header-content">
            <div className="header-info">
              <h1 className="page-title">👁️ Gestion des Observateurs</h1>
              <p className="page-subtitle">Gérez les comptes observateurs</p>
            </div>
            <button className="btn btn-primary" onClick={openAddModal}>
              <Plus size={20} />
              Nouvel Observateur
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <AlertCircle size={20} />
            {error}
            <button
              onClick={() => setError('')}
              className="btn-close"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="filters-card">
          <div className="search-container">
            <div className="search-input">
              <Search size={18} />
              <input
                type="text"
                placeholder="Rechercher un observateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-field"
              />
            </div>
            <div className="filter-group">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actifs seulement</option>
                <option value="inactive">Inactifs seulement</option>
              </select>
              <button
                onClick={fetchObservateurs}
                className="btn-icon blue"
                title="Actualiser"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Observateurs List */}
        <div className="commercials-card">
          <div className="section-header">
            <h2 className="section-title">
              <Users size={24} className="icon-green" />
              Liste des Observateurs ({filteredObservateurs.length})
            </h2>
            <div className="export-actions">
              <button className="btn btn-secondary">
                <Download size={16} />
                Exporter
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Chargement des observateurs...</p>
            </div>
          ) : (
            <div className="commercials-grid">
              {filteredObservateurs.length > 0 ? (
                filteredObservateurs.map(observateur => (
                  <div key={observateur._id} className="commercial-item">
                    <div className="commercial-header">
                      <h3 className="commercial-name">{observateur.nom}</h3>
                      <div className="commercial-badges">
                        <button
                          onClick={() => handleToggleActive(observateur)}
                          className={`badge ${observateur.actif ? 'green-badge' : 'gray-badge'} clickable-badge`}
                          title={`Cliquer pour ${observateur.actif ? 'désactiver' : 'activer'}`}
                        >
                          {observateur.actif ? (
                            <>
                              <CheckCircle size={12} />
                              Actif
                            </>
                          ) : (
                            <>
                              <XCircle size={12} />
                              Inactif
                            </>
                          )}
                        </button>
                      </div>
                      <div className="commercial-actions">
                        <button
                          onClick={() => openEditModal(observateur)}
                          className="btn-icon yellow"
                          title="Modifier"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setObservateurToDelete(observateur);
                            setShowDeleteModal(true);
                          }}
                          className="btn-icon red"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="commercial-details">
                      {observateur.telephone && (
                        <div className="detail-item">
                          <Phone size={14} />
                          {observateur.telephone}
                        </div>
                      )}
                      {observateur.email && (
                        <div className="detail-item">
                          <Mail size={14} />
                          {observateur.email}
                        </div>
                      )}
                      <div className="detail-small">
                        <Calendar size={12} />
                        Créé le {new Date(observateur.createdAt).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data-message">
                  <Users size={48} />
                  <p>Aucun observateur trouvé</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal for Add/Edit Observateur */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal large">
              <div className="modal-header">
                <h3 className="modal-title">
                  {editingObservateur ? 'Modifier Observateur' : 'Nouvel Observateur'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn-close"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleCreateOrUpdateObservateur}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Nom complet *</label>
                      <input
                        type="text"
                        value={newObservateur.nom}
                        onChange={e => setNewObservateur({ ...newObservateur, nom: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Téléphone *</label>
                      <input
                        type="tel"
                        value={newObservateur.telephone}
                        onChange={e => setNewObservateur({ ...newObservateur, telephone: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={newObservateur.email}
                        onChange={e => setNewObservateur({ ...newObservateur, email: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        {editingObservateur ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}
                      </label>
                      <div className="password-input-container">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={newObservateur.motDePasse}
                          onChange={e => setNewObservateur({ ...newObservateur, motDePasse: e.target.value })}
                          required={!editingObservateur}
                          placeholder={editingObservateur ? "Nouveau mot de passe..." : "Mot de passe..."}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="password-toggle"
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    </div>

                    {newObservateur.motDePasse && (
                      <div className="form-group">
                        <label>Confirmer le mot de passe *</label>
                        <input
                          type={showPassword ? "text" : "password"}
                          value={newObservateur.confirmPassword}
                          onChange={e => setNewObservateur({ ...newObservateur, confirmPassword: e.target.value })}
                          placeholder="Confirmer le mot de passe..."
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-checkboxes">
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newObservateur.actif}
                          onChange={e => setNewObservateur({ ...newObservateur, actif: e.target.checked })}
                        />
                        <span className="checkbox-text">
                          <CheckCircle size={16} />
                          Compte actif
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary"
                    >
                      {loading ? 'Sauvegarde...' : editingObservateur ? 'Modifier' : 'Ajouter'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="btn btn-secondary"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && observateurToDelete && (
          <div className="modal-overlay">
            <div className="modal small">
              <h3 className="modal-title">Confirmer la suppression</h3>
              <p className="modal-text">
                Êtes-vous sûr de vouloir supprimer l'observateur <strong>{observateurToDelete.nom}</strong> ?
                Cette action est irréversible.
              </p>
              <div className="modal-actions">
                <button
                  onClick={handleDeleteObservateur}
                  disabled={loading}
                  className="btn btn-danger"
                >
                  {loading ? 'Suppression...' : 'Supprimer'}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setObservateurToDelete(null);
                  }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ObservateurPage;